<script lang="ts">
    // célula do Zgrid2 — independente do zgrid_line.svelte. o checkbox de seleção fica no zgrid2.svelte (depende
    // da linha), e o menu "⋯" dos botões também (um popup só pro grid inteiro): aqui só se dispara "menu".
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faEllipsisH } from '@fortawesome/free-solid-svg-icons'
    import Zinput from './zinput.svelte'
    import { GridColTypeEnum, GridItemShow, formatContent, isEmptyContent, isNumericType } from './zgrid2'
    import type { GridBadge, GridButton, GridCol, GridSlot } from './zgrid2'

    export let col: GridCol
    export let slot: GridSlot
    export let isMobile: boolean = false
    export let emptyValue: string = '—'

    const dispatch = createEventDispatcher<{ menu: { anchor: HTMLElement; buttons: GridButton[] } }>()

    // os valores do GridBadgeClassEnum são os mesmos do Zgrid, mas aqui viram tons próprios — as classes
    // zbadge_* do css_z.css não são aplicadas, pra não misturar o visual dos dois grids
    const BADGE_TONES: Record<string, string> = {
        zbadge_danger: 'danger',
        zbadge_alert: 'alert',
        zbadge_success: 'success',
        zbadge_blue: 'blue',
        zbadge_purple: 'purple',
        zbadge_disabled: 'disabled',
        zbadge_default: 'neutral',
    }

    function badgeClasses(badge: GridBadge): string[] {
        return String(badge?.class || '').split(/\s+/)
    }

    function badgeTone(badge: GridBadge): string {
        return BADGE_TONES[badgeClasses(badge).find((name) => BADGE_TONES[name])] || 'neutral'
    }

    function badgeExtraClass(badge: GridBadge): string {
        return badgeClasses(badge)
            .filter((name) => name && !name.startsWith('zbadge_'))
            .join(' ')
    }

    function buttonTone(color: GridButton['color']): string {
        if (color === 'green' || color === 'primary') return 'tone-green'
        if (color === 'red') return 'tone-red'
        if (color === 'yellow') return 'tone-yellow'
        return ''
    }

    function isShownOnDevice(button: GridButton): boolean {
        return !!button && (!isMobile || button.mobile !== GridItemShow.hide)
    }

    // data, hora e número são curtos e não devem quebrar em duas linhas (na tela incomoda, no papel dobra a altura
    // de cada linha da tabela). texto comum continua quebrando normalmente
    const TIPOS_CURTOS = [
        GridColTypeEnum.date,
        GridColTypeEnum.datetime,
        GridColTypeEnum.datetime_friendly,
        GridColTypeEnum.time,
        GridColTypeEnum.time_short,
    ]

    $: content = slot?.content
    $: tight = isNumericType(col.type) || TIPOS_CURTOS.includes(col.type)
    $: buttons = col.type === GridColTypeEnum.button && Array.isArray(content) ? content : []
    // desktop: a posição importa — o 1º botão fica à mostra (null nessa posição guarda o lugar, pra linha que não tem
    // essa ação, ex.: conta já paga sem "Pagar") e os demais vão pro menu "⋯"
    $: firstButton = isShownOnDevice(buttons[0]) ? buttons[0] : null
    $: otherButtons = buttons.slice(1).filter(isShownOnDevice)
    // celular: até 3 botões, todos à mostra (no card há espaço e o toque no "⋯" custa mais); mais de 3, os 2
    // primeiros à mostra e o "⋯" com os demais. null aqui não guarda lugar, só é ignorado
    $: mobileButtons = buttons.filter(isShownOnDevice)
    $: shownButtons = !isMobile ? (firstButton ? [firstButton] : []) : mobileButtons.length <= 3 ? mobileButtons : mobileButtons.slice(0, 2)
    $: restButtons = !isMobile ? otherButtons : mobileButtons.length <= 3 ? [] : mobileButtons.slice(2)
    // retrocompatibilidade: botão só de ícone, sem label nem title (comum nas telas do Zgrid), não tem texto pra
    // aparecer no menu — continua à mostra na linha. informando title, ele passa a ir pro "⋯"
    $: inlineButtons = restButtons.filter((button) => !button.label && !button.title)
    $: menuButtons = restButtons.filter((button) => button.label || button.title)
    $: badges = col.type === GridColTypeEnum.badge ? (Array.isArray(content) ? content : [content]).filter((badge) => badge && badge.label) : []
    $: empty = isEmptyContent(content)
    $: display = formatContent(col.type, content, emptyValue, isMobile)
    $: truncated = col.type === GridColTypeEnum.string && col.maxLength > 3 && !empty && display.length > col.maxLength
    $: shown = truncated ? display.substring(0, col.maxLength - 3) + '...' : display

    function openMenu(event: MouseEvent) {
        dispatch('menu', { anchor: event.currentTarget as HTMLElement, buttons: menuButtons })
    }
</script>

{#if col.type === GridColTypeEnum.button}
    <div class="z2cell_buttons" class:is-mobile={isMobile}>
        {#each shownButtons as button}
            <button
                type="button"
                class="z2rbtn {buttonTone(button.color)} {button.class || ''}"
                class:is-icon={!button.label}
                disabled={button.disabled}
                title={button.title || button.label || ''}
                on:click={(event) => button.callback?.(event)}
            >
                {#if button.icon}<Fa icon={button.icon} />{/if}
                {#if button.label}<span>{button.label}</span>{/if}
            </button>
        {/each}
        {#each inlineButtons as button}
            <button
                type="button"
                class="z2rbtn is-icon {buttonTone(button.color)} {button.class || ''}"
                disabled={button.disabled}
                on:click={(event) => button.callback?.(event)}
            >
                {#if button.icon}<Fa icon={button.icon} />{/if}
            </button>
        {/each}
        {#if menuButtons.length > 0}
            <button type="button" class="z2rbtn is-icon" title="Mais ações" aria-label="Mais ações" on:click={openMenu}>
                <Fa icon={faEllipsisH} />
            </button>
        {/if}
    </div>
{:else if col.type === GridColTypeEnum.zinput}
    {#if slot?.content}
        <Zinput
            type={slot.content.type}
            class={slot.content.class}
            bind:value={slot.content.value}
            options={slot.content.options}
            on:change={() => slot.content.callback?.(slot.content.value)}
            disabled={slot.content.disabled}
            label={slot.content.label}
            help={slot.content.help}
            placeholder={slot.content.placeholder || ''}
        ></Zinput>
    {/if}
{:else if col.type === GridColTypeEnum.toggle}
    {#if slot?.content && (!isMobile || slot.content.mobile !== GridItemShow.hide)}
        <div class="form-check form-switch z2cell_switch {slot.content.class || ''}">
            <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                disabled={slot.content.disabled}
                bind:checked={slot.content.checked}
                on:change={() => slot.content.callback?.(slot.content.checked)}
            />
            {#if slot.content.label}<span class="z2cell_switch_label">{slot.content.label}</span>{/if}
        </div>
    {/if}
{:else if col.type === GridColTypeEnum.badge}
    {#if badges.length === 0}
        <span class="z2cell_empty">{emptyValue}</span>
    {:else}
        {#each badges as badge}
            <span
                class="z2badge tone-{badgeTone(badge)} {badgeExtraClass(badge)}"
                class:is-custom={!!badge.color}
                style={badge.color ? `--z2-badge-color:${badge.color};` : ''}
            >
                <span class="z2badge_dot"></span>{badge.label}
            </span>
        {/each}
    {/if}
{:else}
    <span class="z2cell_main" class:is-bold={col.bold} class:is-num={isNumericType(col.type)} class:is-tight={tight} class:z2cell_empty={empty} title={truncated ? display : null}
        >{shown}</span
    >
    {#if slot?.badge?.label && !slot.subtitle}
        <span class="z2tag tone-{badgeTone(slot.badge)} {badgeExtraClass(slot.badge)}">{slot.badge.label}</span>
    {/if}
    {#if slot?.action}
        <button
            type="button"
            class="z2cell_action {slot.action.class || ''}"
            title={slot.action.title || ''}
            disabled={slot.action.disabled}
            on:click={(event) => slot.action.callback?.(event)}
        >
            {#if slot.action.icon}<Fa icon={slot.action.icon} />{/if}
            {#if slot.action.label}<span>{slot.action.label}</span>{/if}
        </button>
    {/if}
    {#if slot?.subtitle}
        <div class="z2cell_sub">
            {slot.subtitle}
            {#if slot.badge?.label}
                <span class="z2tag tone-{badgeTone(slot.badge)} {badgeExtraClass(slot.badge)}">{slot.badge.label}</span>
            {/if}
        </div>
    {/if}
{/if}

<style>
    .z2cell_main.is-bold {
        font-weight: 600;
    }
    .z2cell_main.is-num {
        font-variant-numeric: tabular-nums;
    }
    .z2cell_main.is-tight {
        white-space: nowrap;
    }
    .z2cell_empty {
        color: var(--z2-text-faint);
    }
    .z2cell_sub {
        margin-top: 2px;
        font-size: 12px;
        line-height: 1.35;
        color: var(--z2-text-muted);
    }

    .z2cell_action {
        border: 0;
        background: transparent;
        color: var(--z2-text-muted);
        padding: 0 2px;
        margin-left: 4px;
        cursor: pointer;
        font-size: 12px;
        vertical-align: baseline;
    }
    .z2cell_action:hover:not(:disabled) {
        color: var(--z2-text);
    }

    /* badge "etiqueta" dentro de uma célula de texto (slot.badge) */
    .z2tag {
        display: inline-block;
        margin-left: 6px;
        padding: 0 6px;
        font-size: 11px;
        font-weight: 500;
        line-height: 16px;
        border: 1px solid var(--z2-border);
        border-radius: 4px;
        color: var(--z2-text-muted);
        vertical-align: 1px;
        white-space: nowrap;
    }
    .z2tag.tone-danger {
        color: var(--z2-danger-text);
        border-color: var(--z2-danger-bg);
    }
    .z2tag.tone-alert {
        color: var(--z2-alert-text);
        border-color: var(--z2-alert-bg);
    }
    .z2tag.tone-success {
        color: var(--z2-success-text);
        border-color: var(--z2-success-bg);
    }
    .z2tag.tone-blue {
        color: var(--z2-blue-text);
        border-color: var(--z2-blue-bg);
    }
    .z2tag.tone-purple {
        color: var(--z2-purple-text);
        border-color: var(--z2-purple-bg);
    }
    .z2tag.tone-disabled {
        opacity: 0.6;
    }

    /* badge "pílula" de uma coluna type=badge */
    .z2badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin: 1px 4px 1px 0;
        /* sem o ajuste de 1px dos botões: texto minúsculo pequeno já fica centralizado (medido no navegador) */
        padding: 0 9px;
        font-size: 12px;
        font-weight: 500;
        line-height: 22px;
        border-radius: 999px;
        white-space: nowrap;
        background: var(--z2-neutral-bg);
        color: var(--z2-neutral-text);
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .z2badge_dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
        flex-shrink: 0;
    }
    .z2badge.tone-danger {
        background: var(--z2-danger-bg);
        color: var(--z2-danger-text);
    }
    .z2badge.tone-alert {
        background: var(--z2-alert-bg);
        color: var(--z2-alert-text);
    }
    .z2badge.tone-success {
        background: var(--z2-success-bg);
        color: var(--z2-success-text);
    }
    .z2badge.tone-blue {
        background: var(--z2-blue-bg);
        color: var(--z2-blue-text);
    }
    .z2badge.tone-purple {
        background: var(--z2-purple-bg);
        color: var(--z2-purple-text);
    }
    .z2badge.tone-disabled {
        opacity: 0.6;
    }
    /* GridBadge.color: a cor informada vira o ponto/texto e um fundo suave, nos dois temas */
    .z2badge.is-custom {
        background: color-mix(in srgb, var(--z2-badge-color) 16%, transparent);
        color: color-mix(in srgb, var(--z2-badge-color) 70%, var(--z2-text));
    }

    .z2cell_buttons {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
    }
    /* no celular podem ser até 3 botões com texto: quebram de linha em vez de estourar a largura do card */
    .z2cell_buttons.is-mobile {
        flex-wrap: wrap;
    }
    .z2rbtn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        height: 30px;
        padding: 0 12px;
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        color: var(--z2-text);
        background: var(--z2-bg);
        border: 1px solid var(--z2-border);
        border-radius: 7px;
        cursor: pointer;
        transition:
            background-color 0.12s,
            border-color 0.12s;
    }
    .z2rbtn:hover:not(:disabled) {
        background: var(--z2-bg-hover);
    }
    /* desce o rótulo 1px sem mexer no ícone: a Nunito deixa o texto acima do centro (ver .z2label no zgrid2.svelte) */
    .z2rbtn > span {
        padding-top: 2px;
    }
    .z2rbtn:disabled {
        opacity: 0.45;
        cursor: default;
    }
    .z2rbtn.is-icon {
        width: 30px;
        padding: 0;
        color: var(--z2-text-muted);
    }
    .z2rbtn.tone-green {
        color: var(--z2-accent-text);
        border-color: var(--z2-accent);
    }
    .z2rbtn.tone-green:hover:not(:disabled) {
        background: var(--z2-accent-soft);
    }
    .z2rbtn.tone-red {
        color: var(--z2-danger-text);
        border-color: var(--z2-danger-text);
    }
    .z2rbtn.tone-red:hover:not(:disabled) {
        background: var(--z2-danger-bg);
    }
    .z2rbtn.tone-yellow {
        color: var(--z2-alert-text);
        border-color: var(--z2-alert-text);
    }
    .z2rbtn.tone-yellow:hover:not(:disabled) {
        background: var(--z2-alert-bg);
    }

    .z2cell_switch {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        min-height: 0;
    }
    .z2cell_switch_label {
        font-size: 13px;
    }

    @media print {
        .z2cell_buttons {
            display: none;
        }
        /* mesma ideia da tabela: no papel o conteúdo da célula não precisa do respiro da tela */
        .z2cell_sub {
            margin-top: 0;
            font-size: 0.85em;
            line-height: 1.2;
        }
        .z2badge {
            margin: 0 3px 0 0;
            padding: 0 5px;
            font-size: 0.85em;
            line-height: 1.4;
        }
        .z2badge_dot {
            width: 5px;
            height: 5px;
        }
        .z2tag {
            margin-left: 4px;
            padding: 0 4px;
            font-size: 0.8em;
            line-height: 1.3;
        }
    }
</style>
